// src/pages/tutorials/TutorialInstallation.jsx
import React, { Component, Fragment } from "react";
import { Helmet } from 'react-helmet';
import HeaderOne from '../../components/header/HeaderOne'; // Adjusted path
import Footer from '../../components/footer/Footer'; // Adjusted path
import ScrollToTop from 'react-scroll-up';
import { FiChevronUp } from "react-icons/fi";
import Breadcrumb from "../../components/common/Breadcrumb"; // Adjusted path

class TutorialInstallation extends Component {
    render() {
        return (
            <Fragment>
                <Helmet>
                    <title>Installation & First Launch | Eidocell Documentation</title>
                    <meta name="description" content="Learn how to install Eidocell, launch it for the first time, and configure key settings for your image analysis workflow." />
                </Helmet>

                <HeaderOne />

                <Breadcrumb title={'Installation, First Launch & Settings'} />

                <div className="main-content-area ptb--120 bg_color--1">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-10 offset-lg-1">
                                <div className="startup-guide-content"> {/* Reusing startup-guide styles */}
                                    <p className="lead">
                                        Welcome to Eidocell! This tutorial will guide you through installing the application on your system,
                                        navigating its initial launch, understanding the main interface components, and configuring essential settings
                                        to tailor Eidocell to your needs.
                                    </p>

                                    <section id="installation" className="mb--40">
                                        <h2>1. Installation</h2>
                                        <p>
                                            Eidocell is designed to be accessible across multiple platforms. Please download the latest version
                                            suitable for your operating system from the <a href="#/download">Download Page</a>.
                                        </p>
                                        
                                        <h3>Windows</h3>
                                        <p>
                                            Download the Eidocell Windows installer (e.g., <code>eidocell-windows-installer.exe</code>).
                                            Once downloaded, double-click the <code>.exe</code> file to run the installer.
                                            Follow the on-screen prompts. You can typically accept the default installation path or choose a custom location.
                                        </p>
                                        <p><code>[SCREENSHOT: Windows installer - welcome screen and directory selection]</code></p>
                                        <p>After installation, you can launch Eidocell from the Start Menu or a desktop shortcut if one was created.</p>

                                        <h3>macOS</h3>
                                        <p>
                                            Download the Eidocell macOS application bundle (e.g., <code>eidocell-macos.dmg</code>).
                                            Double-click the <code>.dmg</code> file to open it. A window will appear showing the Eidocell application icon and a shortcut to your Applications folder.
                                            Drag the Eidocell icon into your Applications folder.
                                        </p>
                                        <p><code>[SCREENSHOT: macOS DMG window showing drag-and-drop to Applications]</code></p>
                                        <p>
                                            You can then eject the disk image. Launch Eidocell from your Applications folder.
                                            <strong>Note:</strong> On the first launch, macOS might show a security warning because the app is from an "unidentified developer." You may need to right-click (or Control-click) the Eidocell app icon, select "Open," and then confirm in the dialog box.
                                        </p>

                                        {/* <h3>Linux (AppImage)</h3>
                                        <p>
                                            For Linux distributions, Eidocell is provided as an AppImage (e.g., <code>Eidocell-linux.AppImage</code>).
                                            After downloading, you'll need to make the AppImage file executable. Open your terminal, navigate to the directory where you downloaded the file, and run:
                                            <pre><code>chmod +x Eidocell-linux.AppImage</code></pre>
                                        </p>
                                        <p><code>[SCREENSHOT: Linux terminal showing the chmod command]</code></p>
                                        <p>
                                            Then, you can run Eidocell directly from the terminal:
                                            <pre><code>./Eidocell-linux.AppImage</code></pre>
                                            Optionally, you can use a tool like AppImageLauncher to integrate AppImages into your system menu for easier access.
                                        </p> */}
                                    </section>

                                    <section id="first-launch" className="mb--40">
                                        <h2>2. First Launch</h2>
                                        <p>
                                            When you launch Eidocell for the first time, you will see the main application window.
                                            Initially, the central workspace area will be disabled as no analysis session is active.
                                        </p>
                                        <p><code>[SCREENSHOT: Eidocell main window on first launch - workspace disabled, "Sessions" button visible]</code></p>
                                        <p>
                                            To begin working, you'll need to either create a new session or open an existing one. This is done via the "Sessions"
                                            icon in the main navigation bar on the left.
                                        </p>
                                    </section>

                                    <section id="interface-overview" className="mb--40">
                                        <h2>3. Interface Overview</h2>
                                        <p>The Eidocell interface is organized into a few key areas:</p>
                                        <ul>
                                            <li>
                                                <strong>Main Navigation Bar (Left):</strong> This vertical bar provides access to global application functions:
                                                <ul>
                                                    <li><strong>Sessions:</strong> Create new analysis sessions or open existing ones.</li>
                                                    <li><strong>Save:</strong> Save the current state of your active session.</li>
                                                    <li><strong>Export:</strong> Export data, images, and plots from your session.</li>
                                                    <li><strong>Settings:</strong> Configure application-wide preferences.</li>
                                                </ul>
                                            </li>
                                            <p><code>[SCREENSHOT: Eidocell main window highlighting the left Main Navigation Bar]</code></p>
                                            <li>
                                                <strong>Workspace Area (Center):</strong> This is the main area where you'll interact with your data. It becomes active once a session is loaded.
                                            </li>
                                            <li>
                                                <strong>Pivot Navigation (Top of Workspace, when session is active):</strong> This horizontal navigation allows you to switch between different analysis views within the active session:
                                                <ul>
                                                    <li><strong>Gallery:</strong> Browse and manage your imported images.</li>
                                                    <li><strong>Classes:</strong> Define and assign custom classes to your images/cells.</li>
                                                    <li><strong>Clusters:</strong> Perform and explore unsupervised clustering of your data.</li>
                                                    <li><strong>Segmentation:</strong> Access tools for cell segmentation and mask generation.</li>
                                                    <li><strong>Analysis:</strong> Create plots and visualize your quantitative data.</li>
                                                </ul>
                                            </li>
                                            <p><code>[SCREENSHOT: Eidocell workspace area active, highlighting the top Pivot Navigation (Gallery, Classes, etc.)]</code></p>
                                        </ul>
                                    </section>

                                    <section id="settings" className="mb--40">
                                        <h2>4. Settings</h2>
                                        <p>
                                            You can customize various aspects of Eidocell by accessing the Settings dialog. Click on the "Settings"
                                            icon (usually a gear icon) in the main navigation bar.
                                        </p>
                                        <p><code>[SCREENSHOT: Main navigation bar with "Settings" icon highlighted, leading to Settings Dialog]</code></p>
                                        <p>Key settings include:</p>
                                        <ul>
                                            <li>
                                                <strong>Theme:</strong> Choose between "Light" and "Dark" visual themes for the application interface.
                                                <p><code>[SCREENSHOT: Settings dialog - General Settings section showing Theme selector]</code></p>
                                            </li>
                                            <li>
                                                <strong>ONNX Execution Provider:</strong> Select the computation backend for neural network models.
                                                "CPUExecutionProvider" is the default and works on all systems. If you have a compatible NVIDIA GPU with CUDA installed,
                                                you can select "CUDAExecutionProvider" for potentially faster AI model processing.
                                                <p><code>[SCREENSHOT: Settings dialog - General Settings section showing ONNX Provider dropdown]</code></p>
                                            </li>
                                            <li>
                                                <strong>Selected Model (Feature Extractor):</strong> Choose the default pre-trained ONNX model that Eidocell will use for deep feature extraction (primarily for clustering). Options might include models like MobileNetV3, DINOv2, etc.
                                                <strong>Important:</strong> Changing this model will invalidate any previously extracted features and clusters, requiring them to be recomputed for the new model.
                                                <p><code>[SCREENSHOT: Settings dialog - Model Management showing "Selected Model" dropdown]</code></p>
                                            </li>
                                            <li>
                                                <strong>Custom Models:</strong> Eidocell allows you to add your own ONNX-compatible feature extractor models.
                                                You can "Add Model" by providing a unique name, the path to the <code>.onnx</code> file, and its output feature dimension.
                                                Custom models you add will appear in the "Selected Model" dropdown.
                                                <p><code>[SCREENSHOT: Settings dialog - Model Management showing custom models list and Add/Remove buttons]</code></p>
                                            </li>
                                            <li>
                                                <strong>Display Settings:</strong> Adjust "Thumbnail Quality" for image previews and "Images per Collage" for how many sample images are shown in class/cluster preview cards.
                                                <p><code>[SCREENSHOT: Settings dialog - Display Settings section]</code></p>
                                            </li>
                                        </ul>
                                        <p>
                                            After making changes, click the "Apply" button to save your settings. Some settings, like a model change,
                                            may prompt you that existing session data (features/clusters) will be invalidated.
                                        </p>
                                    </section>

                                    <section id="next-steps">
                                        <h2>Next Steps</h2>
                                        <p>
                                            Now that you have Eidocell installed and are familiar with its basic interface and settings,
                                            you're ready to start analyzing your images.
                                        </p>
                                        <p>
                                            Proceed to the next tutorial: <a href="#/usage/tutorials/importing-data">Importing Your First Dataset</a>.
                                        </p>
                                    </section>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Footer />

                <div className="backto-top">
                    <ScrollToTop showUnder={160}>
                        <FiChevronUp />
                    </ScrollToTop>
                </div>
            </Fragment>
        )
    }
}

export default TutorialInstallation;